
import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { cloudClient as supabase } from '@/integrations/supabase/cloudClient';
import { cachedFetch, IDENTITY_STALE_MS, queryClient } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { clearUserScopedStorage } from '@/lib/userScopedStorage';
import { markSessionKicked, clearSessionKicked } from '@/lib/sessionKick';


interface AuthContextType {
  user: User | null;
  session: Session | null;
  userRole: string | null;
  loading: boolean;
  /** True once the initial session restore + role lookup have completed at least once. */
  authReady: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, fullName?: string, companyName?: string, planKeys?: string[]) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  resendConfirmation: (email: string) => Promise<{ error: any }>;
  isAdmin: boolean;
  lastActivity: number;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Session timeout: 30 minutes of inactivity
const SESSION_TIMEOUT = 30 * 60 * 1000;

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const { toast } = useToast();

  // Track in-flight role lookups so a stale resolution can't overwrite newer state
  const roleRequestIdRef = useRef(0);
  const mountedRef = useRef(true);
  // Tracks the user id we've already fully synced (role fetched). Used to skip
  // redundant work on TOKEN_REFRESHED / tab-focus events that produce a new
  // session object for the same user.
  const syncedUserIdRef = useRef<string | null>(null);
  // Distinguish user-initiated sign-out from server-side kick (session pruned
  // because a newer sign-in exceeded the per-user session limit).
  const intentionalSignOutRef = useRef(false);


  // One user_roles request per user per minute, however many callers ask.
  const fetchUserRole = (userId: string, attempt = 0): Promise<string | null> =>
    attempt === 0
      ? cachedFetch(['user_roles', userId], () => fetchUserRoleUncached(userId), IDENTITY_STALE_MS)
      : fetchUserRoleUncached(userId, attempt);

  const fetchUserRoleUncached = async (userId: string, attempt = 0): Promise<string | null> => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      if (error) {
        console.error('Error fetching user role:', error);
        if (attempt < 2) {
          await new Promise(r => setTimeout(r, 400));
          return fetchUserRoleUncached(userId, attempt + 1);
        }
        return null;
      }

      const roles = data?.map(item => item.role) || [];
      if (roles.includes('admin')) return 'admin';

      // Empty result early on → retry (RLS race after fresh sign-in)
      if (roles.length === 0 && attempt < 2) {
        await new Promise(r => setTimeout(r, 400));
        return fetchUserRoleUncached(userId, attempt + 1);
      }

      return roles.length > 0 ? roles[0] : null;
    } catch (error) {
      console.error('Error fetching user role:', error);
      return null;
    }
  };

  /**
   * Single funnel for syncing session state. Both initial getSession() and
   * onAuthStateChange events go through here, so admin/role state is never
   * temporarily downgraded during startup.
   */
  const syncSession = async (nextSession: Session | null) => {
    const requestId = ++roleRequestIdRef.current;
    const nextUserId = nextSession?.user?.id ?? null;

    if (!mountedRef.current) return;

    // Fast-path: same user as already synced (e.g. silent token refresh on tab
    // focus). Refresh the in-memory session for the new access token but skip
    // re-setting user / re-fetching role so downstream contexts don't churn.
    if (nextUserId && nextUserId === syncedUserIdRef.current) {
      setSession(nextSession);
      setLoading(false);
      setAuthReady(true);
      return;
    }

    setSession(nextSession);
    setUser(nextSession?.user ?? null);

    if (nextSession?.user) {
      updateActivity();
      const role = await fetchUserRole(nextSession.user.id);
      // Discard if a newer sync has started
      if (!mountedRef.current || requestId !== roleRequestIdRef.current) return;
      setUserRole(role);
      syncedUserIdRef.current = nextSession.user.id;
    } else {
      setUserRole(null);
      syncedUserIdRef.current = null;
    }

    if (!mountedRef.current) return;
    setLoading(false);
    setAuthReady(true);
  };

  const updateActivity = () => {
    setLastActivity(Date.now());
  };

  const checkSessionTimeout = () => {
    if (session && Date.now() - lastActivity > SESSION_TIMEOUT) {
      signOut();
      toast({
        title: "Session Expired",
        description: "Your session has expired due to inactivity. Please sign in again.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    mountedRef.current = true;

    const activities = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    activities.forEach(activity => {
      document.addEventListener(activity, updateActivity, true);
    });

    const timeoutCheck = setInterval(checkSessionTimeout, 60000);

    // Failed email-verification clicks (expired/invalidated link) redirect
    // here with #error=access_denied&error_code=... in the URL hash. Route
    // them to /auth (hash preserved) so the page can show a user-facing
    // explanation instead of silently landing on the home page.
    if (
      typeof window !== 'undefined' &&
      window.location.hash.includes('error=') &&
      !window.location.pathname.startsWith('/auth')
    ) {
      window.location.replace('/auth' + window.location.hash);
      return;
    }

    // 1. Register listener FIRST so we never miss an event during init
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        if (event === 'PASSWORD_RECOVERY') {
          if (typeof window !== 'undefined' && window.location.pathname !== '/reset-password') {
            window.location.replace('/reset-password' + window.location.hash);
          }
          return;
        }
        // If we get a SIGNED_OUT event and the user didn't click Sign Out, the
        // session was pruned server-side by another device. Do NOT hard-redirect:
        // public pages stay viewable. Flag it instead — ProtectedRoute sends them
        // to /auth?kicked=1 only when they open a protected page.
        if (event === 'SIGNED_OUT' && !intentionalSignOutRef.current) {
          markSessionKicked();
          clearUserScopedStorage();
        }

        intentionalSignOutRef.current = false;
        setTimeout(() => {
          syncSession(nextSession);
          // On fresh sign-in, ask the DB to trim older sessions to the per-user limit.
          if (event === 'SIGNED_IN' && nextSession?.user) {
            clearSessionKicked();
            supabase.rpc('prune_my_sessions' as any).then(() => {}, () => {});
          }

        }, 0);
      }
    );



    // 2. THEN check for an existing session — funneled through the same helper
    supabase.auth.getSession().then(({ data: { session: existing } }) => {
      syncSession(existing);
    });

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
      clearInterval(timeoutCheck);
      activities.forEach(activity => {
        document.removeEventListener(activity, updateActivity, true);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password,
      });

      if (error) {
        const msg = (error.message || '').toLowerCase();
        const code = (error as any)?.code || '';
        let userFriendlyMessage: string;
        if (msg.includes('invalid login credentials')) {
          userFriendlyMessage = 'Invalid email or password. Please check your credentials and try again.';
        } else if (msg.includes('email not confirmed') || code === 'email_not_confirmed') {
          userFriendlyMessage = 'Please confirm your email before signing in. Use the link in the most recent confirmation email — older links stop working once a new one is sent. No email? Use Resend confirmation below.';
        } else if (msg.includes('email rate limit') || msg.includes('rate limit')) {
          userFriendlyMessage = 'Too many attempts. Please wait a minute and try again.';
        } else {
          userFriendlyMessage = error.message || 'Unable to sign in at this time. Please try again later.';
        }

        toast({
          title: "Sign In Failed",
          description: userFriendlyMessage,
          variant: "destructive",
        });
        // On error we still need to release the loading state
        setLoading(false);
      } else {
        updateActivity();
        toast({
          title: "Welcome back!",
          description: "You have been successfully signed in.",
        });
        // loading will be cleared by syncSession via onAuthStateChange
      }

      return { error };
    } catch (error) {
      const genericError = error instanceof Error ? error : new Error('An unexpected error occurred. Please try again.');
      toast({
        title: "Sign In Failed",
        description: genericError.message,
        variant: "destructive",
      });
      setLoading(false);
      return { error: genericError };
    }
  };

  const signUp = async (email: string, password: string, fullName?: string, companyName?: string, planKeys?: string[]) => {
    try {
      setLoading(true);
      // Land confirmed users on the sign-in page with a success banner so
      // they explicitly sign in with the credentials they just created.
      const redirectUrl = `${window.location.origin}/auth?confirmed=1`;

      const { error } = await supabase.auth.signUp({
        email: email.toLowerCase().trim(),
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName?.trim(),
            company_name: companyName?.trim(),
            plan_keys: Array.isArray(planKeys) ? planKeys : [],
          },
        },
      });


      if (error) {
        const userFriendlyMessage = error.message.includes('already registered')
          ? 'An account with this email already exists. Please sign in instead.'
          : error.message || 'Unable to create account at this time. Please try again later.';
          
        toast({
          title: "Sign Up Failed",
          description: userFriendlyMessage,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Account Created",
          description: "Please check your email to confirm your account.",
        });
      }

      return { error };
    } catch (error) {
      const genericError = error instanceof Error ? error : new Error('An unexpected error occurred. Please try again.');
      toast({
        title: "Sign Up Failed",
        description: genericError.message,
        variant: "destructive",
      });
      return { error: genericError };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      // Optimistically clear local state IMMEDIATELY so the next render has user=null.
      // This closes the window where stale approvalStatus could leak access to protected routes.
      intentionalSignOutRef.current = true;
      roleRequestIdRef.current++; // discard any in-flight role lookup
      setUser(null);
      setSession(null);
      setUserRole(null);

      // Shared-machine hygiene: drop this person's project, branding and saved
      // work from the browser. Consent and unit preferences are kept.
      clearUserScopedStorage();
      clearSessionKicked();

      queryClient.removeQueries({ queryKey: ['user_roles'] });
      queryClient.removeQueries({ queryKey: ['entitlements'] });

      await supabase.auth.signOut();
      // syncSession will run via onAuthStateChange and confirm the cleared state
      toast({
        title: "Signed Out",
        description: "You have been successfully signed out.",
      });

    } catch (error) {
      console.error('Sign out error:', error);
      toast({
        title: "Error",
        description: "There was an issue signing out. Please try again.",
        variant: "destructive",
      });
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setLoading(true);
      const redirectUrl = `${window.location.origin}/reset-password`;

      const { error } = await supabase.auth.resetPasswordForEmail(
        email.toLowerCase().trim(),
        { redirectTo: redirectUrl }
      );


      if (error) {
        toast({
          title: "Reset Failed",
          description: "Unable to send reset email. Please try again.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Check Your Email",
          description: "If an account exists with this email, you will receive a password reset link.",
        });
      }

      return { error };
    } catch (error) {
      toast({
        title: "Reset Failed",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
      return { error };
    } finally {
      setLoading(false);
    }
  };

  const resendConfirmation = async (email: string) => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email.toLowerCase().trim(),
        // Match signUp()'s redirect so every confirmation email (original or
        // resent) lands on the same post-confirm page.
        options: { emailRedirectTo: `${window.location.origin}/auth?confirmed=1` },
      });

      if (error) {
        const msg = (error.message || '').toLowerCase();
        const description = msg.includes('rate limit')
          ? 'Too many attempts. Please wait a minute and try again.'
          : 'Unable to resend confirmation email. Please try again later.';
        toast({ title: 'Resend Failed', description, variant: 'destructive' });
      } else {
        toast({
          title: 'Confirmation email sent',
          description: 'Check your inbox for the confirmation link.',
        });
      }
      return { error };
    } catch (error) {
      toast({
        title: 'Resend Failed',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
      return { error };
    }
  };

  const isAdmin = userRole === 'admin';

  const value: AuthContextType = {
    user,
    session,
    userRole,
    loading,
    authReady,
    signIn,
    signUp,
    signOut,
    resetPassword,
    resendConfirmation,
    isAdmin,
    lastActivity,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
